/**
 * Chassis GPS-tracking status for the order form's chassis-location pin.
 *
 * A chassis only has a live position when BOTH are true: the unit is an active
 * row in the GWI equipment list (`cms.equipmentlist`, category CHASSIS), and
 * BlackBerry Radar reports coordinates for it (`bb_radar_chassis`). The pin is
 * green for `tracked` and red otherwise, so dispatch can tell before clicking.
 */
export type ChassisTrackingStatus =
  /** In the GWI equipment list and reporting to BB Radar — a map click will work. */
  | 'tracked'
  /** In the GWI equipment list, but BB Radar has no position for the unit. */
  | 'not-tracked'
  /** No active chassis row in the GWI equipment list (foreign/retired/typo'd unit). */
  | 'not-in-equipment-list'
  /** Equipment list not loaded yet (or failed) — don't claim red or green. */
  | 'unknown';

/** Minimal shape needed from `/orders/chassis-equipment`. */
export interface ChassisTrackingEquipment {
  unit: string;
  hasRadar?: boolean;
}

const normalize = (unit: string | null | undefined): string =>
  (unit ?? '').trim().toUpperCase();

/**
 * Resolve a chassis unit's tracking status against the loaded GWI chassis list.
 * `equipment` empty means "not loaded yet" — the caller's list always has rows
 * once `/orders/chassis-equipment` resolves — so that yields 'unknown' rather
 * than falsely reporting every unit as missing from the equipment list.
 */
export function getChassisTrackingStatus(
  unit: string | null | undefined,
  equipment: ReadonlyArray<ChassisTrackingEquipment> | null | undefined,
): ChassisTrackingStatus {
  const target = normalize(unit);
  if (!target) return 'unknown';
  if (!equipment || equipment.length === 0) return 'unknown';
  const match = equipment.find((e) => normalize(e.unit) === target);
  if (!match) return 'not-in-equipment-list';
  return match.hasRadar ? 'tracked' : 'not-tracked';
}

/** Tooltip / aria text explaining the pin's color. */
export function chassisTrackingTitle(status: ChassisTrackingStatus): string {
  switch (status) {
    case 'not-in-equipment-list':
      return 'Show Chassis Location — not in the GWI equipment list, no tracking expected';
    case 'not-tracked':
      return 'Show Chassis Location — no BlackBerry Radar position for this chassis';
    default:
      return 'Show Chassis Location';
  }
}
